import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

interface GhostContextType {
  isGhostOpen: boolean;
  openGhost: () => void;
  closeGhost: () => void;
  toggleGhost: () => void;
  isRecordingShadow: boolean;
  startShadowRecord: () => void;
  stopShadowRecord: () => void;
  hotkey: string;
  setHotkey: (hotkey: string) => void;
}

const GhostContext = createContext<GhostContextType | undefined>(undefined);

// Default hotkey: Ctrl+Z (or Cmd+Z on Mac)
const DEFAULT_HOTKEY = 'ctrl+z';

// Parse hotkey string into modifier keys
const parseHotkey = (hotkey: string): { ctrl: boolean; alt: boolean; shift: boolean; meta: boolean; key: string } => {
  const parts = hotkey.toLowerCase().split('+');
  const key = parts[parts.length - 1];
  return {
    ctrl: parts.includes('ctrl') || parts.includes('control'),
    alt: parts.includes('alt'),
    shift: parts.includes('shift'),
    meta: parts.includes('meta') || parts.includes('cmd') || parts.includes('command'),
    key,
  };
};

export const GhostProvider = ({ children }: { children: ReactNode }) => {
  const [isGhostOpen, setIsGhostOpen] = useState(false);
  const [isRecordingShadow, setIsRecordingShadow] = useState(false);
  const [hotkey, setHotkeyState] = useState(() => {
    // Load from localStorage or use default
    if (typeof window !== 'undefined') {
      return localStorage.getItem('superjarvis_ghost_hotkey') || DEFAULT_HOTKEY;
    }
    return DEFAULT_HOTKEY;
  });

  const openGhost = useCallback(() => setIsGhostOpen(true), []);
  const closeGhost = useCallback(() => setIsGhostOpen(false), []);
  const toggleGhost = useCallback(() => setIsGhostOpen(prev => !prev), []);

  const startShadowRecord = useCallback(() => {
    setIsRecordingShadow(true);
    setIsGhostOpen(true); // Always open the HUD when recording
  }, []);

  const stopShadowRecord = useCallback(() => {
    setIsRecordingShadow(false);
    setIsGhostOpen(false);
  }, []);

  const setHotkey = useCallback((newHotkey: string) => {
    setHotkeyState(newHotkey.toLowerCase());
    if (typeof window !== 'undefined') {
      localStorage.setItem('superjarvis_ghost_hotkey', newHotkey.toLowerCase());
    }
  }, []);

  // Global keyboard shortcut listener
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const parsed = parseHotkey(hotkey);

      // Check if all modifier keys match
      const modifiersMatch =
        e.ctrlKey === parsed.ctrl &&
        e.altKey === parsed.alt &&
        e.shiftKey === parsed.shift &&
        e.metaKey === parsed.meta;

      // Check if the key matches
      const keyMatches = e.key.toLowerCase() === parsed.key;

      if (modifiersMatch && keyMatches) {
        e.preventDefault();
        e.stopPropagation();
        toggleGhost();
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [hotkey, toggleGhost]);

  return (
    <GhostContext.Provider value={{
      isGhostOpen,
      openGhost,
      closeGhost,
      toggleGhost,
      isRecordingShadow,
      startShadowRecord,
      stopShadowRecord,
      hotkey,
      setHotkey,
    }}>
      {children}
    </GhostContext.Provider>
  );
};

export const useGhost = (): GhostContextType => {
  const context = useContext(GhostContext);
  if (!context) {
    throw new Error('useGhost must be used within a GhostProvider');
  }
  return context;
};

export default GhostContext;
